import { useForm } from "react-hook-form";
import FormField from "../../components/FormFiel/FormFiel";
import { useState } from "react";
import { useAuthContext } from "../../context/authContext";
import { IoAlertCircleOutline } from "react-icons/io5";
import { FiCheckCircle, FiEye, FiEyeOff } from "react-icons/fi";

function Login() {
  const { login } = useAuthContext();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      await login(data); // Suponiendo que login() es tu función de autenticación con Firebase
    } catch (error) {
      // Extraer el código del error de Firebase
      const errorCode = error.code || "UNKNOWN_ERROR";

      switch (errorCode) {
        case "auth/invalid-credential":
          setError(
            "El nombre de usuario o contraseña son incorrectos. Intenta nuevamente."
          );
          break;
        case "auth/user-disabled":
          setError(
            "Este usuario ha sido deshabilitado. Contacta al administrador."
          );
          break;
        case "auth/too-many-requests":
          setError(
            "Has intentado iniciar sesión demasiadas veces. Intenta más tarde."
          );
          break;
        default:
          setError(
            `Ocurrió un error inesperado (${errorCode}). Intenta nuevamente.`
          );
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    "Cobros y comprobantes al instante",
    "Pagos y liquidaciones a propietarios",
    "Aumentos y vencimientos bajo control",
  ];

  return (
    <div className="antialiased min-h-screen bg-white md:bg-gray-50 flex items-center justify-center px-0 md:px-4 pt-28 md:pt-36 pb-10 md:pb-12">
      <div className="w-full max-w-4xl lg:max-w-5xl xl:max-w-6xl grid md:grid-cols-2 bg-white md:rounded-3xl md:shadow-xl md:ring-1 md:ring-gray-200/70 overflow-hidden">
        {/* Panel de marca (solo escritorio) */}
        <div className="relative hidden md:flex flex-col justify-between p-10 lg:p-14 xl:p-16 text-white bg-gradient-to-br from-secondary via-primary to-secondary overflow-hidden">
          {/* Formas decorativas suaves */}
          <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 lg:w-72 lg:h-72 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 w-64 h-64 lg:w-80 lg:h-80 rounded-full bg-white/5" />

          <span className="relative text-xs lg:text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
            Panel de gestión
          </span>

          <div className="relative py-8 lg:py-10">
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
              Toda tu gestión de alquileres, en un solo lugar.
            </h2>
            <p className="mt-4 lg:mt-6 text-white/80 lg:text-lg max-w-md">
              Ordená la administración de tu inmobiliaria con datos claros y en
              tiempo real.
            </p>
          </div>

          <ul className="relative space-y-3 lg:space-y-4">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm lg:text-base text-white/90">
                <FiCheckCircle className="w-5 h-5 lg:w-6 lg:h-6 shrink-0 text-white" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Formulario */}
        <div className="p-8 sm:p-10 lg:p-14 xl:p-16 flex flex-col justify-center">
          <div className="mb-6 lg:mb-8">
            <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
              Bienvenido de nuevo 👋
            </h1>
            <p className="text-sm lg:text-base text-gray-500 mt-1 lg:mt-2">
              Ingresá tus datos para acceder al panel.
            </p>
          </div>

          {/* Mensaje de error si hay problemas con el login */}
          {error && (
            <p className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
              <IoAlertCircleOutline className="text-xl shrink-0" /> {error}
            </p>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4 lg:space-y-5 [&_input]:pr-11 lg:[&_input]:py-3.5 lg:[&_input]:text-base lg:[&_label]:text-base">
              <FormField
                label="Correo Electrónico"
                id="email"
                name="email"
                required={true}
                type="email"
                maxLength={35}
                minLength={6}
                placeholder="tucorreo@ejemplo.com"
                autoComplete="email"
                errors={errors}
                register={register}
              />
              <FormField
                label="Contraseña"
                id="password"
                name="contraseña"
                required={true}
                type={showPassword ? "text" : "password"}
                minLength={8}
                maxLength={16}
                placeholder="••••••••"
                autoComplete="current-password"
                errors={errors}
                register={register}
                toggleIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-3 bottom-2.5 lg:bottom-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <FiEyeOff className="w-5 h-5" />
                    ) : (
                      <FiEye className="w-5 h-5" />
                    )}
                  </button>
                }
              />
            </div>

            <button
              type="submit"
              className={`mt-6 lg:mt-8 w-full flex justify-center items-center bg-primary hover:bg-secondary text-white py-3 lg:py-4 lg:text-lg rounded-xl tracking-wide font-semibold shadow-lg shadow-primary/25 transition-colors duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg
                    role="status"
                    className="inline h-5 w-5 animate-spin mr-2 text-white/40 fill-white"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  Cargando...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
